import { Injectable } from '@angular/core';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import { ColorUtils } from '../utils/color.utils';

export interface SymbolConfigLike {
  opacity?: number;
}

export interface LineSymbolConfigLike extends SymbolConfigLike {
  outlineWidth?: number;
  outlineColor?: string;
  outlineStyle?:
    | 'dash'
    | 'dash-dot'
    | 'dot'
    | 'long-dash'
    | 'long-dash-dot'
    | 'long-dash-dot-dot'
    | 'none'
    | 'short-dash'
    | 'short-dash-dot'
    | 'short-dash-dot-dot'
    | 'short-dot'
    | 'solid';
}

export interface PolygonSymbolConfigLike extends LineSymbolConfigLike {
  fillColor?: string;
}

export interface PointSymbolConfigLike extends PolygonSymbolConfigLike {
  size?: number;
  pointStyle?:
    | 'circle'
    | 'square'
    | 'cross'
    | 'x'
    | 'diamond'
    | 'triangle'
    | 'path';
}

export interface TextSymbolConfigLike extends SymbolConfigLike {
  text?: string;
  fontSize?: number;
  fontColor?: string;
}

const defaultColor = '#000000';

@Injectable()
export class MapSymbolService {
  createPolygonSymbol(config: PolygonSymbolConfigLike) {
    const fillColor = ColorUtils.alpha(
      config.fillColor ?? defaultColor,
      config.opacity
    ).rgba();
    const outlineSymbol = this.createLineSymbol(config);

    return new SimpleFillSymbol({
      color: fillColor,
      outline: outlineSymbol,
    });
  }

  createLineSymbol(config: LineSymbolConfigLike) {
    const outlineColor = ColorUtils.alpha(
      config.outlineColor ?? defaultColor,
      config.opacity
    ).rgba();

    return new SimpleLineSymbol({
      color: outlineColor,
      width: config.outlineWidth ?? 0,
      style: config.outlineStyle ?? 'solid',
    });
  }

  createPointSymbol(config: PointSymbolConfigLike) {
    const fillColor = ColorUtils.alpha(
      config.fillColor ?? defaultColor,
      config.opacity
    ).rgba();
    const outlineSymbol = this.createLineSymbol(config);

    return new SimpleMarkerSymbol({
      style: config.pointStyle ?? 'circle',
      size: config.size ?? 0,
      color: fillColor,
      outline: outlineSymbol,
    });
  }

  createTextSymbol(config: TextSymbolConfigLike) {
    const fontColor = ColorUtils.alpha(
      config.fontColor ?? defaultColor,
      config.opacity
    ).rgba();

    return new TextSymbol({
      text: config.text ?? '',
      color: fontColor,
      font: {
        size: config.fontSize ?? 12,
      },
    });
  }
}
